import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { ScrollArea, ScrollBar } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { cn } from "@/core/utils/utils";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { ApprovalMark } from "@/payroll/approvals/approval-mark";
import { describeMark } from "@/payroll/approvals/approval-types";
import { getSheetApproval } from "@/payroll/approvals/utils";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { SheetSwitcher } from "./sheet-switcher";
import { useSheetTabScroll } from "./use-sheet-tab-scroll";
import { SHEET_PREFETCH_DWELL_MS } from "./utils";

// Excel-style sheet tab pinned to the bottom of the workbook frame. The sheet
// name leads and its compact approval mark trails without crowding the scroll
// controls at the end of the strip.
const SHEET_TAB_CLASSNAME = cn(
  "flex max-w-64 shrink-0 items-center gap-1.5 whitespace-nowrap border-r border-r-stroke-tertiary border-t-2 border-t-transparent py-1.5 pl-3 pr-4",
  "text-md text-tertiary-text transition-colors",
  "hover:bg-surface-primary hover:text-secondary-text",
  "data-[state=active]:border-t-stroke-tabSelected data-[state=active]:bg-surface-primary data-[state=active]:font-semibold data-[state=active]:text-primary-text",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
);

interface SheetTabListProps {
  activeSheetId: string;
  isSheetSwitcherOpen: boolean;
  sheets: IAiSpreadsheetSheet[];
  onSheetSwitcherOpenChange: (isOpen: boolean) => void;
  onSheetSelect: (sheetId: string) => void;
  onSheetPrefetchIntent: (sheetId: string) => void;
  /** Drives the per-tab approval marks. Setting them lives in the toolbar. */
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  rightAccessory?: ReactNode;
}

export const SheetTabList = ({
  activeSheetId,
  isSheetSwitcherOpen,
  sheets,
  onSheetSwitcherOpenChange,
  onSheetSelect,
  onSheetPrefetchIntent,
  approvalStatus,
  rightAccessory,
}: SheetTabListProps) => {
  const activeSheet = sheets.find((sheet) => sheet.id === activeSheetId);
  const {
    activeTabRef,
    canScrollLeft,
    canScrollRight,
    scrollTabs,
    tabListRef,
    tabViewportRef,
  } = useSheetTabScroll({ activeSheetId, sheets });
  const { schedule: scheduleSheetPrefetch, cancel: cancelSheetPrefetch } =
    useDelayedAction<string>(onSheetPrefetchIntent, SHEET_PREFETCH_DWELL_MS);

  return (
    <Stack
      direction="row"
      align="stretch"
      fullWidth
      gap="none"
      className="shrink-0 border-t border-stroke-tertiary bg-surface-secondary"
    >
      <SheetSwitcher
        activeSheetId={activeSheetId}
        isOpen={isSheetSwitcherOpen}
        sheets={sheets}
        onOpenChange={onSheetSwitcherOpenChange}
        onSheetSelect={onSheetSelect}
        onSheetPrefetchIntent={onSheetPrefetchIntent}
      />

      <ScrollArea
        hideDefaultScrollBar
        className="quiet-scrollbar min-w-0 flex-1"
        type="auto"
        viewportRef={tabViewportRef}
      >
        <TabsPrimitive.List
          ref={tabListRef}
          className="flex min-w-max items-stretch"
        >
          {sheets.map((sheet) => {
            const approval = getSheetApproval(sheet, approvalStatus);

            return (
              <Stack
                key={sheet.id}
                direction="row"
                align="stretch"
                gap="none"
                className="group relative"
              >
                <TabsPrimitive.Trigger
                  ref={sheet.id === activeSheetId ? activeTabRef : undefined}
                  value={sheet.id}
                  title={sheet.name}
                  className={SHEET_TAB_CLASSNAME}
                  onMouseEnter={() => scheduleSheetPrefetch(sheet.id)}
                  onMouseLeave={cancelSheetPrefetch}
                  onFocus={() => scheduleSheetPrefetch(sheet.id)}
                  onBlur={cancelSheetPrefetch}
                >
                  <span className="min-w-0 truncate">{sheet.name}</span>
                  {approval && (
                    <span className="sr-only">{describeMark(approval)}</span>
                  )}
                  {approval && (
                    <ApprovalMark accessKey={approval} withTooltip />
                  )}
                </TabsPrimitive.Trigger>
              </Stack>
            );
          })}
        </TabsPrimitive.List>
        <ScrollBar orientation="horizontal" className="h-1.5 p-0" />
      </ScrollArea>

      <SheetTabScrollButton
        direction="left"
        disabled={!canScrollLeft}
        onClick={() => scrollTabs(-1)}
      />
      <SheetTabScrollButton
        direction="right"
        disabled={!canScrollRight}
        onClick={() => scrollTabs(1)}
      />

      {rightAccessory}

      <span className="sr-only" aria-live="polite">
        {activeSheet ? `Current sheet: ${activeSheet.name}` : "No sheet"}
      </span>
    </Stack>
  );
};

interface SheetTabScrollButtonProps {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
}

const SheetTabScrollButton = ({
  direction,
  disabled,
  onClick,
}: SheetTabScrollButtonProps) => {
  const Icon = direction === "left" ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      aria-label={`Scroll sheet tabs ${direction}`}
      disabled={disabled}
      onClick={onClick}
      className="relative z-10 flex w-8 shrink-0 items-center justify-center border-r border-r-stroke-tertiary bg-surface-secondary text-tertiary-text transition-colors hover:bg-surface-primary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-stroke-secondary"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
};
